/**
 * LegacyDataBanner — UX1A Platform Primitive
 *
 * Non-invasive amber banner surfaced on pages that rely on a non-canonical
 * data source. Deliberately avoids exposing technical provenance (e.g.
 * "MongoDB") to operational users. Technical details remain in admin/audit views.
 *
 * Variants:
 *   banner   — full-width top-of-page bar (default)
 *   inline   — compact inline notice, embeds inside a section
 *
 * Usage:
 *   <LegacyDataBanner module="Teaching & Learning" />
 *   <LegacyDataBanner module="Alumni & Engagement" canonicalRoute="/alumni-canonical" />
 *   <LegacyDataBanner variant="inline" module="Research & Innovation" />
 */
import React, { useState } from "react";
import { AlertTriangle, X, ArrowRight } from "lucide-react";

export function LegacyDataBanner({
  module,
  canonicalRoute,
  canonicalLabel,
  variant = "banner",
  dismissible = true,
  "data-testid": testId,
}) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  const message = module
    ? `${module} currently uses a legacy data source and is not yet connected to the canonical AcademiaOS record.`
    : "This module currently uses a legacy data source and is not yet connected to the canonical AcademiaOS record.";

  if (variant === "inline") {
    return (
      <div
        data-testid={testId || "legacy-data-banner-inline"}
        className="flex items-start gap-2.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800"
        role="status"
      >
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
        <span className="leading-relaxed">{message}</span>
        {canonicalRoute && (
          <a
            href={canonicalRoute}
            className="inline-flex items-center gap-1 font-medium text-amber-800 underline hover:text-amber-900 whitespace-nowrap"
          >
            {canonicalLabel || "View canonical record"}
            <ArrowRight className="h-3 w-3" />
          </a>
        )}
      </div>
    );
  }

  // Banner variant — full-width bar
  return (
    <div
      data-testid={testId || "legacy-data-banner"}
      className="flex items-center gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2.5 md:px-8 text-xs text-amber-800"
      role="status"
    >
      <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
      <span className="flex-1 leading-relaxed">{message}</span>
      {canonicalRoute && (
        <a
          href={canonicalRoute}
          className="inline-flex items-center gap-1 font-medium text-amber-800 underline hover:text-amber-900 whitespace-nowrap"
        >
          {canonicalLabel || "View canonical record"}
          <ArrowRight className="h-3 w-3" />
        </a>
      )}
      {dismissible && (
        <button
          onClick={() => setDismissed(true)}
          className="rounded p-0.5 hover:bg-amber-100 text-amber-600 transition-colors"
          aria-label="Dismiss"
          data-testid="legacy-banner-dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
